'use client';

import React, { useState, useCallback, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import Image from 'next/image';
import BrowserCompatWarning from './BrowserCompatWarning';
import StatCards from './StatCards';
import ProcessingLog from './ProcessingLog';
import ProgressSection from './ProgressSection';
import FolderPickerHero from './FolderPickerHero';
import { processPdfsFromFiles } from '../lib/pdfProcessor';
import type { ProcessingResult, ProcessingStats } from '../lib/types';

export default function PdfExtractorClient() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [stats, setStats] = useState<ProcessingStats>({
    total: 0,
    renamed: 0,
    warnings: 0,
    errors: 0,
  });
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [folderName, setFolderName] = useState<string>('');
  const abortRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startProcessingFiles = useCallback(async (files: File[], folder: string) => {
    abortRef.current = false;
    setIsProcessing(true);
    setIsDone(false);
    setResults([]);
    setStats({ total: 0, renamed: 0, warnings: 0, errors: 0 });
    setProgress({ current: 0, total: 0 });
    setFolderName(folder);

    try {
      await processPdfsFromFiles({
        files,
        abortRef,
        onProgress: (current, total) => setProgress({ current, total }),
        onResult: (result) => {
          setResults((prev) => [result, ...prev]);
          setStats((prev) => ({
            total: prev.total + 1,
            renamed: prev.renamed + (result.status === 'success' ? 1 : 0),
            warnings: prev.warnings + (result.status === 'warning' ? 1 : 0),
            errors: prev.errors + (result.status === 'error' ? 1 : 0),
          }));
        },
        onDone: (finalStats) => {
          setIsDone(true);
          setIsProcessing(false);
          if (finalStats.errors === 0 && finalStats.warnings === 0) {
            toast.success(
              `✓ ${finalStats.renamed} PDF${finalStats.renamed !== 1 ? 's' : ''} renomeado${finalStats.renamed !== 1 ? 's' : ''} com sucesso`
            );
          } else {
            toast.warning(
              `Concluído: ${finalStats.renamed} renomeados, ${finalStats.warnings} avisos, ${finalStats.errors} erros`
            );
          }
        },
        onError: (msg) => {
          toast.error(msg);
          setIsProcessing(false);
        },
      });
    } catch (err: any) {
      toast.error(`Erro ao processar os ficheiros: ${err?.message ?? 'Erro desconhecido'}`);
      setIsProcessing(false);
    }
  }, []);

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;

      const pdfFiles = Array.from(fileList).filter((f) =>
        f.name.toLowerCase().endsWith('.pdf')
      );

      if (pdfFiles.length === 0) {
        toast.error('Nenhum ficheiro PDF encontrado na pasta selecionada.');
        // Reset input so same folder can be selected again
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Derive folder name from the first file's webkitRelativePath
      const firstPath = (pdfFiles[0] as any).webkitRelativePath as string | undefined;
      const folder = firstPath ? firstPath.split('/')[0] : 'pasta selecionada';

      // Reset input for future selections
      if (fileInputRef.current) fileInputRef.current.value = '';

      await startProcessingFiles(pdfFiles, folder);
    },
    [startProcessingFiles]
  );

  const handleSelectFolder = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleAbort = useCallback(() => {
    abortRef.current = true;
    setIsProcessing(false);
    setIsDone(true);
    toast.info('Processamento interrompido pelo utilizador.');
  }, []);

  const handleReset = useCallback(() => {
    abortRef.current = true;
    setIsProcessing(false);
    setIsDone(false);
    setResults([]);
    setStats({ total: 0, renamed: 0, warnings: 0, errors: 0 });
    setProgress({ current: 0, total: 0 });
    setFolderName('');
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="bottom-right" richColors closeButton />

      {/* Hidden folder input */}
      <input
        ref={fileInputRef}
        type="file"
        // @ts-ignore — webkitdirectory is not in standard TS types
        webkitdirectory=""
        multiple
        accept=".pdf"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-card border-b border-border shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 h-auto py-3 flex items-center gap-6">
          {/* Logo — left, 200% size */}
          <Image
            src="/assets/images/ZEZERECONTA_04-1781872355054.png"
            alt="ZezereConta Logo"
            width={280}
            height={80}
            className="object-contain flex-shrink-0"
            priority
          />
          {/* Text content — right of logo */}
          <div className="flex flex-1 items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-foreground text-base leading-tight tracking-tight">
                Extrair Nome
              </span>
              <span className="text-xs leading-tight text-white">
                Renomeação automática de PDFs
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground hidden sm:block">
                Segurança Social · Portugal
              </span>
              <div className="w-2 h-2 rounded-full bg-accent pulse-dot" title="Aplicação ativa" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-8 space-y-6">
        <BrowserCompatWarning />

        <FolderPickerHero
          isProcessing={isProcessing}
          isDone={isDone}
          folderName={folderName}
          onSelectFolder={handleSelectFolder}
          onAbort={handleAbort}
          onReset={handleReset}
        />

        {(isProcessing || isDone || results.length > 0) && (
          <div className="slide-up space-y-6">
            <StatCards stats={stats} isProcessing={isProcessing} />

            {(isProcessing || progress.total > 0) && (
              <ProgressSection
                current={progress.current}
                total={progress.total}
                isProcessing={isProcessing}
              />
            )}

            <ProcessingLog results={results} isProcessing={isProcessing} />
          </div>
        )}
      </main>

      <footer className="mt-12 border-t border-border bg-card">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Processamento 100% local — os seus ficheiros nunca saem do browser.
          </p>
          <p className="text-xs text-muted-foreground text-tabular">
            PDF.js (Mozilla) · File System Access API · Chrome / Edge
          </p>
        </div>
      </footer>
    </div>
  );
}